import { type Comments as CommentsProps } from '@web/graphql-components'
import { Comments as PageComments } from '@web/components/page-components/Comments'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { usePageStore } from '../../../libs/hooks/usePageStore'
import { useEffect, useState } from 'react'
import { useMediaQuery } from '@web/components/utils/chakra-hooks'
import { getCommentsQueryOptions, mapComment } from './utils'
import ViewAllDialog from './ViewAllDialog'
import PaginatedComments from './PaginatedComments'
import { GIVEEASY_CORRELATION_HEADER } from 'apps/fe-donation/app/api/utils'
import { v4 } from 'uuid'
import { toSearchParams } from 'apps/fe-donation/utils'

dayjs.extend(relativeTime)

const Comments = (props: CommentsProps) => {
  const pageId = usePageStore((state) => state.pageId)
  const campaignDomain = usePageStore((state) => state.campaignDomain) ?? ''
  const [isMobile] = useMediaQuery(['(max-width: 900px)'], { fallback: [] })
  const [, setRerender] = useState(0)
  const options = getCommentsQueryOptions(props)

  const { data, isPending } = useQuery({
    queryKey: ['comments', options],
    queryFn: () => {
      const comments = fetch(`/api/comments?${toSearchParams(options)}`, {
        headers: {
          'Content-Type': 'application/json',
          [GIVEEASY_CORRELATION_HEADER]: v4(),
        },
        next: {
          revalidate: 60,
        },
      })
        .then((res) => res.json())
        .catch(() => ({ comments: [] }))

      return comments
    },
    enabled: !!pageId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  useEffect(() => {
    if (isPending || !data) {
      return
    }

    setTimeout(() => {
      setRerender((prev) => prev + 1)
    }, 1000)
  }, [data, isPending])

  return (
    <PageComments
      {...props}
      isLoading={isPending}
      totalRecords={data?.comments?.totalRecords}
      comments={data?.comments?.comments?.map((c) => mapComment(c, props, campaignDomain)) ?? []}
      ViewAllDialog={ViewAllDialog}
      PaginatedComments={PaginatedComments}
      isMobile={isMobile}
    />
  )
}

export default Comments
